export const WEBINAR_LAYOUT_PRESETS = [
  "speaker_only",
  "speaker_slides",
  "slides_only",
  "gallery",
  "picture_in_picture",
  "side_by_side",
  "panel_discussion",
  "interview",
  "presentation_focus",
  "cinema",
] as const;

export type WebinarLayoutPreset = (typeof WEBINAR_LAYOUT_PRESETS)[number];

export type WebinarLayoutTransition = "crossfade" | "cut" | "slide";

export interface WebinarLayoutPresetDefinition {
  id: WebinarLayoutPreset;
  label: string;
  description: string;
  shortcut: string;
  liveOutputSupported: true;
}

export interface UnsupportedVisionWebinarLayoutPresetDefinition {
  id: string;
  label: string;
  description: string;
  shortcut: null;
  liveOutputSupported: false;
  disabledReason: string;
}

export const WEBINAR_LAYOUT_PRESET_DEFINITIONS: WebinarLayoutPresetDefinition[] = [
  {
    id: "speaker_only",
    label: "Speaker Only",
    description: "Single-stage focus for keynote or direct address moments.",
    shortcut: "1",
    liveOutputSupported: true,
  },
  {
    id: "speaker_slides",
    label: "Speaker + Slides",
    description: "Balanced stage for presenter-led teaching with supporting slides.",
    shortcut: "2",
    liveOutputSupported: true,
  },
  {
    id: "slides_only",
    label: "Slides Only",
    description: "Presentation-first layout with speaker minimized to a support role.",
    shortcut: "3",
    liveOutputSupported: true,
  },
  {
    id: "gallery",
    label: "Gallery",
    description: "Equal-emphasis layout for multi-person sessions and audience energy.",
    shortcut: "4",
    liveOutputSupported: true,
  },
  {
    id: "picture_in_picture",
    label: "Picture in Picture",
    description: "Main stage with a compact secondary frame for speaker continuity.",
    shortcut: "5",
    liveOutputSupported: true,
  },
  {
    id: "side_by_side",
    label: "Side by Side",
    description: "Two-up layout for demos, walkthroughs, and paired presentation.",
    shortcut: "6",
    liveOutputSupported: true,
  },
  {
    id: "panel_discussion",
    label: "Panel Discussion",
    description: "Host-centered panel framing with conversation-supporting side rail.",
    shortcut: "7",
    liveOutputSupported: true,
  },
  {
    id: "interview",
    label: "Interview",
    description: "One-on-one conversation layout with balanced moderator attention.",
    shortcut: "8",
    liveOutputSupported: true,
  },
  {
    id: "presentation_focus",
    label: "Presentation Focus",
    description: "Large teaching stage with restrained supporting chrome.",
    shortcut: "9",
    liveOutputSupported: true,
  },
  {
    id: "cinema",
    label: "Cinema",
    description: "Immersive widescreen stage with minimal distraction during story beats.",
    shortcut: "0",
    liveOutputSupported: true,
  },
];

export const UNSUPPORTED_VISION_WEBINAR_LAYOUT_PRESET_DEFINITIONS: UnsupportedVisionWebinarLayoutPresetDefinition[] = [
  {
    id: "canvas_mode",
    label: "Canvas Mode",
    description: "Interactive canvas full screen with speaker continuity.",
    shortcut: null,
    liveOutputSupported: false,
    disabledReason: "Disabled until the presenter canvas runtime can promote staged canvas sections into live output.",
  },
  {
    id: "offer_mode",
    label: "Offer Mode",
    description: "Product or offer spotlight with CTA overlay.",
    shortcut: null,
    liveOutputSupported: false,
    disabledReason: "Disabled until offer overlays are wired into the live-output compositor from the control room.",
  },
  {
    id: "video_playback",
    label: "Video Playback",
    description: "Pre-recorded video playback with optional speaker continuity.",
    shortcut: null,
    liveOutputSupported: false,
    disabledReason: "Disabled until the webinar runtime can mount and synchronize prerecorded playback as a live output source.",
  },
  {
    id: "countdown",
    label: "Countdown",
    description: "Branded full-screen countdown for waiting rooms and breaks.",
    shortcut: null,
    liveOutputSupported: false,
    disabledReason: "Disabled until timed rundown/countdown state is mounted in the live-output runtime.",
  },
  {
    id: "custom",
    label: "Custom",
    description: "Drag-arranged presenter composition for power users.",
    shortcut: null,
    liveOutputSupported: false,
    disabledReason: "Disabled until a custom composition editor and renderer are mounted for webinar live output.",
  },
];

export const WEBINAR_LAYOUT_SHORTCUTS: Record<string, WebinarLayoutPreset> = Object.fromEntries(
  WEBINAR_LAYOUT_PRESET_DEFINITIONS.map((preset) => [preset.shortcut, preset.id]),
) as Record<string, WebinarLayoutPreset>;
